<template>
    <transition name="slide-right">
        <div v-if="isVisible" class="ai-sidebar">
            <div class="ai-sidebar-inner">
                <div class="ai-header">
                    <div class="ai-title">
                        <simple-svg :src="StarsIcon" custom-class-name="ai-icon" @click="goBack"></simple-svg>
                        <h2>AI Ассистент</h2>
                    </div>
                    <div class="ai-header-actions">
                        <image-button
                            v-if="isMobile"
                            :src="MenuIcon"
                            :hint="'Чаты'"
                            @click="sessionDrawerOpen = true"
                        />
                        <image-button :src="CloseIcon" :hint="'Закрыть'" @click="close" />
                    </div>
                </div>

                <div class="content-wrapper">
                    <component :is="(sessionId || isMobile) ? AiChat : AiSessionList" />
                </div>

                <!-- Drawer с сессиями (только мобиле) -->
                <transition name="drawer-slide">
                    <div v-if="sessionDrawerOpen" class="sessions-drawer">
                        <div class="sessions-drawer__header">
                            <h3>Чаты</h3>
                            <image-button :src="CloseIcon" :hint="'Закрыть'" @click="sessionDrawerOpen = false" />
                        </div>
                        <AiSessionList @session-selected="sessionDrawerOpen = false" />
                    </div>
                </transition>
                <div v-if="sessionDrawerOpen" class="sessions-drawer-overlay" @click="sessionDrawerOpen = false" />
            </div>
        </div>
    </transition>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useNavigationStore } from '@/store/NavigationStore';
import { useAgentStore } from '@/store/AgentStore';
import { storeToRefs } from 'pinia';
import CloseIcon from '@/assets/icons/Close_MD.svg';
import StarsIcon from '@/assets/icons/Stars.svg';
import MenuIcon from '@/assets/icons/Bar_Left.svg';
import AiChat from '@/components/ai/AiChat.vue';
import AiSessionList from '@/components/ai/AiSessionList.vue';

const navigationStore = useNavigationStore();
const isVisible = computed(() => navigationStore.isAiChatVisible);
const sessionDrawerOpen = ref(false);
const isMobile = ref(window.innerWidth <= 768);

const onResize = () => { isMobile.value = window.innerWidth <= 768; };
onMounted(() => window.addEventListener('resize', onResize));

const agentStore = useAgentStore();
const { sessionId, sessions } = storeToRefs(agentStore);

const close = () => {
    navigationStore.toggleAiChat();
}

const goBack = () => {
    agentStore.sessionId = null;
}

watch(() => agentStore.sessionId, () => {
    sessionDrawerOpen.value = false;
});

watch(isVisible, async (newVal) => {
    if (newVal) {
        await agentStore.fetchSessions();
        if (sessions.value.length > 0) {
            const sortedSessions = [...sessions.value].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            agentStore.selectSession(sortedSessions[0].id);
        } else {
            await agentStore.createSession();
        }
    }
});

onMounted(async () => {
    if (isVisible.value) {
        await agentStore.fetchSessions();
        if (sessions.value.length > 0) {
            const sortedSessions = [...sessions.value].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            agentStore.selectSession(sortedSessions[0].id);
        } else {
            await agentStore.createSession();
        }
    }
});
</script>

<style lang="scss">
.ai-sidebar {
    position: fixed;
    top: 0;
    right: 0;
    width: 600px;
    height: 100%;
    z-index: 1001;
    /* Above navbar if needed, though on other side */
    padding: 10px;
    box-sizing: border-box;
}

.ai-sidebar-inner {
    background-color: var(--color-bg);
    border-radius: 10px;
    /* Matching NavigationBar styling */
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 15px;
    box-sizing: border-box;
    box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
    position: relative;
    overflow: hidden;
}

.ai-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    position: relative;
    z-index: 2;
    flex-shrink: 0;

    .ai-title {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .ai-header-actions {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .ai-icon {
        cursor: pointer;

        path {
            fill: var(--color-blue) !important;
        }
    }

    h2 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
    }

    // Бургер скрыт на широком экране
    .mobile-sessions-btn {
        display: none;
    }
}

.sessions-drawer {
    display: none;
}

.sessions-drawer-overlay {
    display: none;
}

.content-wrapper {
    flex: 1;
    overflow: hidden;
    position: relative;
}

/* Transitions */
.slide-right-enter-active,
.slide-right-leave-active {
    transition: transform 0.3s ease-in-out;
}

.slide-right-enter-from,
.slide-right-leave-to {
    transform: translateX(100%);
}

@media (max-width: 768px) {
    .ai-sidebar {
        width: 100%;
        padding: 0;
        z-index: 1100;

        .ai-sidebar-inner {
            border-radius: 0;
            box-shadow: none;
            padding: 12px;
        }
    }

    .ai-header .mobile-sessions-btn {
        display: flex;
    }

    .sessions-drawer-overlay {
        display: block;
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        z-index: 10;
    }

    .sessions-drawer {
        display: flex;
        flex-direction: column;
        position: absolute;
        top: 0;
        left: 0;
        width: 75%;
        max-width: 300px;
        height: 100%;
        background: var(--color-bg);
        z-index: 11;
        padding: 16px;
        box-sizing: border-box;
        box-shadow: 4px 0 16px rgba(0, 0, 0, 0.15);

        &__header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 16px;

            h3 {
                margin: 0;
                font-size: 16px;
                font-weight: 600;
            }
        }
    }

    .drawer-slide-enter-active,
    .drawer-slide-leave-active {
        transition: transform 0.25s ease;
    }

    .drawer-slide-enter-from,
    .drawer-slide-leave-to {
        transform: translateX(-100%);
    }
}
</style>
